import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Typography,
  Stepper,
  Step,
  StepLabel,
  IconButton,
} from '@mui/material';
import {
  Email,
  VpnKey,
  MarkEmailRead,
  Close as CloseIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useThemeMode } from '../../contexts/ThemeModeContext';

const ForgotPasswordDialog = ({ open, onClose }) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const [step, setStep] = useState(0); // 0: Enter Email, 1: Check Inbox
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const steps = ['Enter Email', 'Check Your Email'];

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/forgot-password', { email });
      if (response.data.success) {
        setStep(1);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset instructions');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(0);
    setEmail('');
    setError('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: '95vw', sm: '460px' },
          maxWidth: '480px !important',
          maxHeight: '85vh',
          borderRadius: '14px',
          bgcolor: isDark ? '#1E1E1E' : '#FFFFFF',
          backgroundImage: 'none',
          color: isDark ? '#F1F5F9' : '#0F172A',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ px: 2.5, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider' }}>
        <Box display="flex" alignItems="center" gap={1}>
          <VpnKey color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: isDark ? '#F1F5F9' : '#0F172A' }}>Reset Password</Typography>
        </Box>
        <IconButton size="small" onClick={loading ? undefined : handleClose} sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, py: 1.75, overflowY: 'auto' }}>
        <Stepper activeStep={step} sx={{ mb: 2, '& .MuiStepLabel-label': { fontSize: '0.8rem' } }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}

        {step === 0 && (
          <form onSubmit={handleEmailSubmit}>
            <Typography variant="body2" sx={{ color: isDark ? '#94A3B8' : 'text.secondary', mb: 1.5 }}>
              Enter your email address and we'll send you a link to reset your password.
            </Typography>

            <TextField
              fullWidth
              size="small"
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              InputProps={{
                startAdornment: <Email color="action" fontSize="small" sx={{ mr: 1 }} />,
              }}
              sx={{ mb: 2 }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={loading || !email}
              sx={{ height: 38 }}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
        )}

        {step === 1 && (
          <Box textAlign="center" py={0.5}>
            <MarkEmailRead color="primary" sx={{ fontSize: 44, mb: 1 }} />
            <Typography variant="h6" fontWeight={700} gutterBottom sx={{ fontSize: '1.05rem' }}>
              Check Your Inbox
            </Typography>
            <Typography variant="body2" sx={{ color: isDark ? '#94A3B8' : 'text.secondary', mb: 1 }}>
              We've sent a password reset link to:
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5, color: isDark ? '#F1F5F9' : '#0F172A' }}>
              {email}
            </Typography>
            <Alert severity="info" sx={{ mb: 1.5, textAlign: 'left', '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
              Click the <strong>Reset Password</strong> button in the email to create a new password. The link expires in <strong>1 hour</strong>.
            </Alert>
            <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : 'text.secondary' }}>
              Didn't receive the email? Check your spam folder, or close this dialog and try again.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: 1, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider', display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={handleClose} disabled={loading} variant={step === 1 ? 'contained' : 'text'} sx={{ color: step === 1 ? undefined : (isDark ? '#94A3B8' : '#64748B') }}>
          {step === 1 ? 'Close' : 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ForgotPasswordDialog;
